<template>
    <div class="daily-view">
        <header class="dynamic-header btn-toolbar justify-content-between">
            <div class="btn-group mt-2">
                <h4 class="font-weight-bold">{{ title }}</h4>
            </div>
            <div>
                <button class="btn btn-outline-primary mr-3" @click="onClose">Fechar</button>
                <div class="btn-group">
                    <button v-if="hasScheduledVouchers" :class="scheduleSelectClass(true)" v-on:click="displayScheduled(true)">
                        <i class="material-icons">schedule</i>
                        <span>Reservas com horário</span>
                    </button>
                    <button v-if="hasNonScheduledVouchers" :class="scheduleSelectClass(false)" v-on:click="displayScheduled(false)">
                        <i class="material-icons">pending_actions</i>
                        <span>Reservas sem horário</span>
                    </button>
                </div>
            </div>
        </header>

        <div class="time-grid mt-4" ref="timeGrid" v-on:resize="handleResize">
            <div class="time-grid-content flex-grid" v-bind:style="columnsStyle">
                <div v-if="withScheduleOnly" v-for="hour in timeList" :class="hour.className" :style="flexGridItemStyle">
                    <div class="hour-segment">
                        <span>{{ hour.label }}</span>
                    </div>
                </div>

                <ReservationItem
                    v-for="posRes in positionedReservations"
                    :key="posRes.reservation.id"
                    :position="posRes.position"
                    :reservation="posRes.reservation"
                    :from="positionInfo.from"
                    :width-unit="unitWidthPx"
                    :height-unit="unitHeightPx"
                    :offset-top="relativeOffsetTopPx"
                    :offset-left="offsetLeftPx"
                    :total-height="totalHeight"
                    :total-width="totalWidth"
                    :z-index-count="zIndexCount"
                    :selected="isSelected(posRes.reservation)"
                    :on-selection="handleSelection"
                    :on-deletion="onReservationDeletion"
                    :on-reschedule="handleReschedule"
                    :on-reallocation="handleReallocation"
                    :ref="makeHighlightedRef(posRes.reservation.id)"
                ></ReservationItem>
            </div>
        </div>
    </div>
</template>

<script>
import { MAX_MINUTE_DAY, minToHour } from '../helpers';
import { checkDate, formatDate } from '../../common/calendar';
import ReservationItem from './ReservationItem';

const mountPositionInfo = (reservations, minuteInterval) => {
    const levels = [];
    let from = null;
    let to = null;

    const posReservations = [];
    for(let reservation of reservations){
        const { schedule_time_minutes, duration_minutes, position } = reservation;

        // If reservation belongs to the previous day, we just ignore it
        if(position < 0 && schedule_time_minutes !== null && schedule_time_minutes + duration_minutes <= MAX_MINUTE_DAY)
            continue;

        // If reservation doesn't have a schedule_time_minutes and doesn't belongs to the current day, just ignore it
        if(position != 0 && schedule_time_minutes === null)
            continue;

        const padding = Math.min(from || 0, 0);
        const posRes = mountPositionedReservation(levels, padding, reservation, minuteInterval);
        const pos = posRes.position;

        if(from === null)
            from = pos.start * (pos.start < 0 ? -1 : 1);

        if(position === 0)
            to = Math.max(to || 0, pos.start + pos.size);

        posReservations.push(posRes);
    }

    const height = Math.max(4, levels.length);

    if(from === null || to === null){
       from = 0;
       to = MAX_MINUTE_DAY / minuteInterval;
    }

    const min = 28;
    let diff = min - (to - from);
    if(diff > 0){
        diff /= 2;
        from -= Math.ceil(diff);
        to += Math.floor(diff);
    }

    return { posReservations, from, to, height };
};

const mountPositionedReservation = (levels, from, reservation, minuteInterval) => {
    // We use the 'position' to fix the time if the reservation doesn't belongs to the current day
    const startMin = (reservation.schedule_time_minutes || 0) + reservation.position * MAX_MINUTE_DAY;

    const position = {
        start: startMin / minuteInterval,
        size: reservation.duration_minutes / minuteInterval,
        row: null
    };

    if(from > position.start)
        from = -position.start;

    loop1:
    for(let index = 0; index < levels.length; index++){
        const level = levels[index];
        for(let x = position.size; x--;){
            const idx = from + position.start + x;
            if(level[idx]){
                continue loop1;
            }
        }

        position.row = index;
        break;
    }

    // Add new level if we couldn't place the reservation
    if(position.row === null){
        position.row = levels.length;
        levels.push([]);
    }

    const level = levels[position.row];
    for(let x = 0; x < position.size; x++){
        const idx = from + position.start + x;
        level[idx] = true;
    }

    return { reservation, position };
};

const scrollToChild = (parent, child) => {
    const parentRect = parent.getBoundingClientRect();
    const childRect = child.getBoundingClientRect();

    const $parent = $(parent);
    $parent.animate({
        scrollLeft: parent.scrollLeft + (childRect.left - parentRect.left) - 50,
        scrollTop: parent.scrollTop + (childRect.top - parentRect.top) - 50
    });
};

export default {
    props: {
        date: Object,
        reservations: Array,
        onClose: Function,
        referenceDate: Date,
        minuteIsNow: Function,
        products: Array,
        onReservationSubmit: Function,
        onReservationDeletion: Function,
        onReservationReschedule: Function,
        onReservationReallocation: Function,
        voucherPageUrl: String,
        highlightedReservation: Object,
        ackHighlightedReservation: Function,
        minuteSetInterval: Number,
    },

    data(){
        return {
            withScheduleOnly: true,
            clickListener: null,
            zIndexCount: 0,
            selectedReservation: null,
            unitWidthPx: 50,
            unitHeightPx: 84,
            offsetTopPx: 70,
            offsetLeftPx: 0
        };
    },

    computed: {
        title(){
            return formatDate(this.date);
        },
        scheduledReservations(){
            return this.reservations.filter(r => r.schedule_time_minutes !== null);
        },
        nonScheduledReservations(){
            return this.reservations.filter(r => r.schedule_time_minutes === null && r.position === 0);
        },
        positionInfo(){
            if(this.withScheduleOnly)
                return mountPositionInfo(this.scheduledReservations, this.minuteSetInterval);

            return mountPositionInfo(this.nonScheduledReservations, this.minuteSetInterval);
        },
        timeList(){
            const arr = [];
            const { from, to } = this.positionInfo;
            const isToday = checkDate(this.date, this.referenceDate);

            for(let x = from; x < to; x++){
                const rawMinutes = x * this.minuteSetInterval;
                const minutes = (MAX_MINUTE_DAY + rawMinutes) % MAX_MINUTE_DAY;

                let className = 'flex-grid-item';
                if(rawMinutes >= 0 && rawMinutes < MAX_MINUTE_DAY){
                    const str = isToday && this.minuteIsNow(minutes, this.minuteSetInterval) ? 'current-time' : 'current-day';
                    className += ` ${str}`;
                }

                arr.push({
                    minutes,
                    label: minToHour(minutes),
                    className
                });
            }

            return arr;
        },
        positionedReservations(){
            return this.positionInfo.posReservations;
        },
        relativeOffsetTopPx(){
            return this.withScheduleOnly ? this.offsetTopPx : 0;
        },
        totalWidth(){
            return this.timeList.length * this.unitWidthPx + this.offsetLeftPx;
        },
        totalHeight(){
            return this.positionInfo.height * this.unitHeightPx + this.relativeOffsetTopPx;
        },
        flexGridStyle(){
            return {
                width: `${this.totalWidth}px`
            };
        },
        flexGridItemStyle(){
            return {
                width: this.unitWidthPx + 'px'
            };
        },
        columnsStyle(){
            const base = this.flexGridStyle;
            const minHeight = `${this.totalHeight + this.relativeOffsetTopPx}px`;

            return { ...base, minHeight };
        },
        hasScheduledVouchers(){
            return this.scheduledReservations.length > 0;
        },
        hasNonScheduledVouchers(){
            return this.nonScheduledReservations.length > 0;
        },
        vouchersUrl(){
            return `${this.voucherPageUrl}?search_date=${this.date.dateString}`;
        }
    },

    methods: {
        isSelected(reservation){
            return this.selectedReservation && reservation.id == this.selectedReservation.id;
        },
        handleSelection(reservation){
            this.selectedReservation = this.isSelected(reservation) ? null : reservation;
            this.zIndexCount++;
        },
        clearSelection(){
            this.selectedReservation = null;
        },
        handleReschedule(...args){
            this.onReservationReschedule(...args);
            this.clearSelection();
        },
        handleReallocation(...args){
            this.onReservationReallocation(...args);
            this.clearSelection();
        },

        setupClickListener(){
            this.clickListener = ev => this.clearSelection();
            this.$refs.timeGrid.addEventListener('click', this.clickListener);
        },
        teardownClickListener(){
            this.$refs.timeGrid.removeEventListener('click', this.clickListener);
        },
        scrollToReservation(){
            const $timeGrid = $(this.$refs.timeGrid);
            const $els = $timeGrid.find('.reservation-item:first');
            if($els.length === 0)
                return;

            const offset = $els.offset();
            $timeGrid.animate({
                scrollLeft: $timeGrid[0].scrollLeft + offset.left - 20
            });
        },
        handleResize(ev){
            this.storeTimeGridSize();
        },
        storeTimeGridSize(){
            const el = this.$refs.timeGrid;
        },
        displayScheduled(enable){
            this.withScheduleOnly = enable;
        },
        scheduleSelectClass(expected){
            return `btn btn-${expected === this.withScheduleOnly ? 'primary' : 'outline-primary'}`;
        },
        makeHighlightedRef(id){
            return `ref-${id}`;
        },
        scrollToHighlighted(){
            if(!this.highlightedReservation)
                return;

            if(this.withScheduleOnly != (this.highlightedReservation.schedule_time_minutes !== null))
               this.withScheduleOnly = !this.withScheduleOnly;

            const ref = this.makeHighlightedRef(this.highlightedReservation.id);
            const component = this.$refs[ref];
            if(component){
                if(!this.isSelected(this.highlightedReservation))
                    this.handleSelection(this.highlightedReservation);

                // We wait 500ms so the element can finish the selection animation before we retrieve its position in 'scrollToChild'
                setTimeout(() => scrollToChild(this.$refs.timeGrid, component[0].$el), 500);
                this.ackHighlightedReservation();
            }
        }
    },

    mounted(){
        this.storeTimeGridSize();
        this.setupClickListener();
        this.scrollToReservation();
        this.scrollToHighlighted();
    },
    beforeUpdate(){
        if(this.withScheduleOnly && !this.hasScheduledVouchers && this.hasNonScheduledVouchers)
            this.withScheduleOnly = false;
    },
    updated(){
        this.scrollToHighlighted();
    },
    beforeDestroy(){
        this.teardownClickListener();
    },

    components: {
        ReservationItem
    }
}
</script>

<style scoped>
.dynamic-header .btn > * {
    vertical-align: middle;
}

h4 {
    margin: 0;
}

.time-grid {
    display: flex;
    flex-direction: column;
    border: 1px solid #aaa;
    border-size: 1px 0;
    height: 400px;
    overflow: auto;
    background: #efefef;
    resize: vertical;
    border-radius: 5px;
}
@media (min-height: 800px) {
    .time-grid {
        height: 600px;
    }
}

.flex-grid {
    display: flex;
}
.flex-grid-item {
    flex: 1 1;
    box-sizing: border-box;
    border-right: 1px solid #ccc;
}
.flex-grid-item:last-child {
    border-right: none;
}
.flex-grid-item.current-day {
    background: #e9f0f3;
}
.flex-grid-item:hover {
    background: #d9e0e3;
}
.flex-grid-item.current-time {
    background: #56e5a6;
}

.time-grid-content {
    flex: 1 1;
    position: relative;
    display: flex;
    overflow: hidden;
}
.time-grid-content .flex-grid-item {
    flex: 1 1;
    height: 100%;
    border-right: 1px solid #ccc;
    box-sizing: border-box;
}
.time-grid-content .flex-grid-item .hour-segment {
    border-bottom: 1px solid #666;
    text-align: center;
}
.time-grid-content .flex-grid-item .hour-segment span {
    display: block;
    height: 50px;
    line-height: 50px;
    cursor: default;
}
.time-grid-content .flex-grid-item .hour-segment button {
    display: block;
    border-width: 0;
    width: 100%;
    margin: 0;
    padding: 0;
    height: 20px;
    line-height: 20px;
    font-size: 16px;
    border-radius: 0;
    background: #ffb835;
}
.time-grid-content .flex-grid-item .hour-segment:hover button {
    background: #eea724;
}
.time-grid-content .flex-grid-item .hour-segment button:hover {
    background: #dd9613;
}
.time-grid-content .flex-grid-item .hour-segment button:active {
    background: #cc8502;
}


.non-scheduled-vouchers-notice {
    border-bottom: 1px solid #c38300;
    padding: 1rem;
    text-align: center;
    background-color: #fcf8e3;
}
</style>
